(() => {
  const config = window.creatorCartConfig || {};
  const creatorMode = window.musetopiaCreatorMode || {};
  const contextKey = 'musetopia:creator-context';
  const CAMPAIGN_PREFIX = 'accio_';
  const buildCampaign = (creatorId) => (creatorId ? `${CAMPAIGN_PREFIX}${creatorId}` : '');
  const parseCreatorId = (value) => {
    const campaign = String(value || '').trim();
    return campaign.startsWith(CAMPAIGN_PREFIX) ? campaign.slice(CAMPAIGN_PREFIX.length) : '';
  };
  const originalFetch = window.fetch.bind(window);

  const readContext = () => {
    try {
      return JSON.parse(sessionStorage.getItem(contextKey) || 'null');
    } catch (_error) {
      return null;
    }
  };

  const normalizeCreatorId = (value) => String(value || 'creator').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
  const initialContext = readContext() || config.context;
  const active = Boolean(config.enabled && config.accessToken && initialContext && initialContext.id);
  // 归因元数据，值会随达人信息异步回填而变化，不参与行项目合并判定
  const identityExcludedKeys = new Set(['_creator_id', '_creator_name']);
  const normalizeAttributeList = (attributes = []) => attributes
    .map((attribute) => ({
      key: String(attribute?.key || '').trim(),
      value: String(attribute?.value || '')
    }))
    .filter((attribute) => attribute.key)
    .sort((left, right) => {
      if (left.key === right.key) return left.value.localeCompare(right.value);
      return left.key.localeCompare(right.key);
    });

  class CreatorCartService {
    constructor() {
      this.context = initialContext;
      this.active = active;
      this.cart = null;
      this.pending = null;
      this.attributionPending = null;
      this.creating = null;
      this.cartFields = `
        id
        checkoutUrl
        totalQuantity
        note
        attributes { key value }
        discountCodes { code applicable }
        cost {
          subtotalAmount { amount currencyCode }
          totalAmount { amount currencyCode }
        }
        lines(first: 100) {
          nodes {
            id
            quantity
            attributes { key value }
            cost {
              amountPerQuantity { amount currencyCode }
              totalAmount { amount currencyCode }
            }
            discountAllocations(lineLevelOnly: false) { discountedAmount { amount currencyCode } }
            merchandise {
              ... on ProductVariant {
                id
                title
                availableForSale
                compareAtPrice { amount currencyCode }
                image { url altText width height }
                product {
                  handle
                  title
                  vendor
                  featuredImage { url altText width height }
                  metafield(key: "Brand", namespace: "product") { value }
                }
              }
            }
          }
        }
      `;
    }

    get storageKey() {
      return `musetopia:creator-cart:${normalizeCreatorId(this.context && this.context.id)}`;
    }

    get cartId() {
      try {
        return sessionStorage.getItem(this.storageKey) || localStorage.getItem(this.storageKey);
      } catch (_error) {
        return null;
      }
    }

    set cartId(value) {
      try {
        if (value) {
          sessionStorage.setItem(this.storageKey, value);
          localStorage.setItem(this.storageKey, value);
        } else {
          sessionStorage.removeItem(this.storageKey);
          localStorage.removeItem(this.storageKey);
        }
      } catch (_error) {}
    }

    async request(query, variables = {}) {
      const response = await originalFetch(config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.accessToken
        },
        body: JSON.stringify({ query, variables })
      });
      const payload = await response.json();
      if (!response.ok || payload.errors) {
        throw new Error(payload.errors?.map((error) => error.message).join(', ') || config.labels.requestError);
      }
      return payload.data;
    }

    assertMutation(payload) {
      const errors = payload?.userErrors || [];
      if (errors.length) throw new Error(errors.map((error) => error.message).join(', '));
      if (!payload?.cart) throw new Error(config.labels.requestError);
      this.cart = payload.cart;
      this.cartId = payload.cart.id;
      this.publish();
      return this.cart;
    }

    creatorAttributes(existing = []) {
      const attributes = new Map((existing || []).map((attribute) => [String(attribute.key), String(attribute.value || '')]));
      attributes.set('utm_campaign', buildCampaign(creatorMode.scaRef));
      attributes.set('_creator_id', String(this.context.id));
      attributes.delete('_creator_name');
      return Array.from(attributes, ([key, value]) => ({ key, value }));
    }

    async ensureCreatorAttributes(cart) {
      if (!cart) return cart;
      const expectedCampaign = buildCampaign(creatorMode.scaRef);
      const attributes = Object.fromEntries((cart.attributes || []).map((attribute) => [attribute.key, attribute.value]));
      if (attributes.utm_campaign === expectedCampaign && attributes._creator_id === String(this.context.id) && attributes._creator_name === undefined) return cart;
      if (this.attributionPending) return this.attributionPending;
      this.attributionPending = this.request(`mutation CreatorCartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(cartId: $cartId, attributes: $attributes) { cart { ${this.cartFields} } userErrors { field message code } }
      }`, { cartId: cart.id, attributes: this.creatorAttributes(cart.attributes) })
        .then((data) => this.assertMutation(data.cartAttributesUpdate))
        .finally(() => { this.attributionPending = null; });
      return this.attributionPending;
    }

    async getCart(force = false) {
      if (!this.active) return null;
      if (this.cart && !force) return this.cart;
      if (this.pending && !force) return this.pending;
      const id = this.cartId;
      if (!id) return null;
      this.pending = this.request(`query CreatorCart($id: ID!) { cart(id: $id) { ${this.cartFields} } }`, { id })
        .then((data) => {
          this.cart = data.cart;
          if (!this.cart) this.cartId = null;
          return this.ensureCreatorAttributes(this.cart);
        })
        .finally(() => { this.pending = null; });
      return this.pending;
    }

    lineAttributes(properties = {}, sellingPlanId = '') {
      const attributes = Object.entries(properties || {})
        .filter(([, value]) => value !== null && value !== undefined && String(value) !== '')
        .map(([key, value]) => ({ key, value: String(value) }));
      if (!attributes.some((attribute) => attribute.key === '_creator_id')) {
        attributes.push({ key: '_creator_id', value: String(this.context.id) });
      }
      if (sellingPlanId && !attributes.some((attribute) => attribute.key === '_selling_plan_id')) {
        attributes.push({ key: '_selling_plan_id', value: String(sellingPlanId) });
      }
      return attributes;
    }

    lineSignature(merchandiseId, sellingPlanId, attributes) {
      return JSON.stringify({
        merchandiseId: this.variantGid(merchandiseId),
        sellingPlanId: sellingPlanId ? this.sellingPlanGid(sellingPlanId) : '',
        attributes: normalizeAttributeList(attributes).filter((attribute) => !identityExcludedKeys.has(attribute.key))
      });
    }

    itemSignature(item) {
      return this.lineSignature(item.id, item.selling_plan, this.lineAttributes(item.properties, item.selling_plan));
    }

    findMatchingLine(cart, item) {
      const signature = this.itemSignature(item);
      return cart?.lines?.nodes?.find((line) => {
        const lineSellingPlanId = (line.attributes || []).find((attribute) => attribute.key === '_selling_plan_id')?.value || '';
        return this.lineSignature(
          line.merchandise.id,
          lineSellingPlanId,
          line.attributes || []
        ) === signature;
      });
    }

    // 创建达人车（含归因 attribute）。并发下用 this.creating 锁串行化，
    // 避免双击/连点时同时发出两个 cartCreate 产生两个孤儿 cart。
    async createCart(lines) {
      if (this.creating) return this.creating;
      this.creating = this.request(`mutation CreatorCartCreate($input: CartInput!) {
        cartCreate(input: $input) { cart { ${this.cartFields} } userErrors { field message code } }
      }`, {
        input: {
          lines,
          attributes: [
            { key: 'utm_campaign', value: buildCampaign(creatorMode.scaRef) },
            { key: '_creator_id', value: String(this.context.id) }
          ]
        }
      }).then((data) => this.assertMutation(data.cartCreate))
        .finally(() => { this.creating = null; });
      return this.creating;
    }

    // 一次性结账车：只含当前单品（对齐 Shopify 原生 Buy it now），直接结算，
    // 不写入持久达人车、不更新 this.cart / this.cartId。归因 attribute 与 createCart 一致。
    async createCheckoutCart(items) {
      const lines = items.map((item) => ({
        merchandiseId: this.variantGid(item.id),
        quantity: Math.max(1, Number(item.quantity) || 1),
        attributes: this.lineAttributes(item.properties, item.selling_plan),
        ...(item.selling_plan ? { sellingPlanId: this.sellingPlanGid(item.selling_plan) } : {})
      }));
      const data = await this.request(`mutation CreatorCartCreate($input: CartInput!) {
        cartCreate(input: $input) { cart { ${this.cartFields} } userErrors { field message code } }
      }`, {
        input: {
          lines,
          attributes: [
            { key: 'utm_campaign', value: buildCampaign(creatorMode.scaRef) },
            { key: '_creator_id', value: String(this.context.id) }
          ]
        }
      });
      const errors = data?.cartCreate?.userErrors || [];
      if (errors.length) throw new Error(errors.map((error) => error.message).join(', '));
      const cart = data?.cartCreate?.cart;
      if (!cart) throw new Error(config.labels.requestError);
      return cart;
    }

    async add(items) {
      const groupedItems = Array.from(items.reduce((map, item) => {
        const signature = this.itemSignature(item);
        const existing = map.get(signature);
        const normalizedItem = {
          id: item.id,
          quantity: Math.max(1, Number(item.quantity) || 1),
          properties: item.properties,
          selling_plan: item.selling_plan
        };
        if (existing) {
          existing.quantity += normalizedItem.quantity;
        } else {
          map.set(signature, normalizedItem);
        }
        return map;
      }, new Map()).values());

      const lines = groupedItems.map((item) => ({
        merchandiseId: this.variantGid(item.id),
        quantity: item.quantity,
        attributes: this.lineAttributes(item.properties, item.selling_plan),
        ...(item.selling_plan ? { sellingPlanId: this.sellingPlanGid(item.selling_plan) } : {})
      }));
      if (!this.cartId) {
        // 无车且有创建在途：等它完成再走「加到已有车」分支，避免并发建出第二个 cart。
        if (this.creating) {
          await this.creating;
          return this.add(items);
        }
        return this.createCart(lines);
      }
      let cart = await this.getCart();
      if (!cart) return this.createCart(lines);

      const linesToAdd = [];
      for (const item of groupedItems) {
        const matchingLine = this.findMatchingLine(cart, item);
        if (matchingLine) {
          const quantity = Number(matchingLine.quantity) + Number(item.quantity || 1);
          const data = await this.request(`mutation CreatorCartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
            cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { ${this.cartFields} } userErrors { field message code } }
          }`, { cartId: cart.id, lines: [{ id: matchingLine.id, quantity }] });
          cart = this.assertMutation(data.cartLinesUpdate);
        } else {
          linesToAdd.push({
            merchandiseId: this.variantGid(item.id),
            quantity: item.quantity,
        attributes: this.lineAttributes(item.properties, item.selling_plan),
            ...(item.selling_plan ? { sellingPlanId: this.sellingPlanGid(item.selling_plan) } : {})
          });
        }
      }

      if (linesToAdd.length) {
        const data = await this.request(`mutation CreatorCartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) { cart { ${this.cartFields} } userErrors { field message code } }
        }`, { cartId: cart.id, lines: linesToAdd });
        cart = this.assertMutation(data.cartLinesAdd);
      }

      return cart;
    }

    async updateLine(lineId, quantity) {
      const cart = await this.getCart();
      const line = this.findLine(cart, lineId);
      if (!line) throw new Error(config.labels.itemMissing);
      if (Number(quantity) <= 0) return this.removeLine(line.id);
      const data = await this.request(`mutation CreatorCartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) { cart { ${this.cartFields} } userErrors { field message code } }
      }`, { cartId: cart.id, lines: [{ id: line.id, quantity: Number(quantity) }] });
      return this.assertMutation(data.cartLinesUpdate);
    }

    async removeLine(lineId) {
      const cart = await this.getCart();
      const line = this.findLine(cart, lineId);
      if (!line) throw new Error(config.labels.itemMissing);
      const data = await this.request(`mutation CreatorCartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { ${this.cartFields} } userErrors { field message code } }
      }`, { cartId: cart.id, lineIds: [line.id] });
      return this.assertMutation(data.cartLinesRemove);
    }

    async clear() {
      const cart = await this.getCart();
      const lineIds = cart?.lines?.nodes?.map((line) => line.id) || [];
      if (!lineIds.length) return cart;
      const data = await this.request(`mutation CreatorCartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) { cart { ${this.cartFields} } userErrors { field message code } }
      }`, { cartId: cart.id, lineIds });
      return this.assertMutation(data.cartLinesRemove);
    }

    async updateNote(note) {
      const cart = await this.ensureCart();
      const data = await this.request(`mutation CreatorCartNoteUpdate($cartId: ID!, $note: String) {
        cartNoteUpdate(cartId: $cartId, note: $note) { cart { ${this.cartFields} } userErrors { field message code } }
      }`, { cartId: cart.id, note: String(note || '') });
      return this.assertMutation(data.cartNoteUpdate);
    }

    // 优惠码。cartDiscountCodesUpdate 是全量覆盖语义（传什么就是什么），所以要先取已有的再追加。
    // 无效码不会报错，只是回来时 applicable 为 false —— 重建列表时把它们滤掉，下次调用即自愈。
    async applyDiscountCode(code) {
      const cart = await this.ensureCart();
      const kept = (cart.discountCodes || []).filter((entry) => entry.applicable).map((entry) => entry.code);
      const next = code && !kept.includes(code) ? kept.concat(code) : kept;
      const data = await this.request(`mutation CreatorCartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) { cart { ${this.cartFields} } userErrors { field message code } }
      }`, { cartId: cart.id, discountCodes: next });
      return this.assertMutation(data.cartDiscountCodesUpdate);
    }

    async ensureCart() {
      const cart = await this.getCart();
      if (cart) return cart;
      return this.add([]);
    }

    checkoutUrlFor(cart) {
      const checkoutUrl = cart?.checkoutUrl;
      const campaign = buildCampaign(creatorMode.scaRef);
      if (!checkoutUrl || !campaign) return checkoutUrl || null;
      try {
        const url = new URL(checkoutUrl, window.location.origin);
        url.searchParams.set('utm_campaign', campaign);
        return url.toString();
      } catch (_error) {
        return checkoutUrl;
      }
    }

    findLine(cart, value) {
      const key = String(value || '');
      return cart?.lines?.nodes?.find((line, index) => {
        const variantId = line.merchandise.id.split('/').pop();
        return line.id === key || variantId === key || String(index + 1) === key;
      });
    }

    variantGid(value) {
      const id = String(value || '');
      return id.startsWith('gid://') ? id : `gid://shopify/ProductVariant/${id}`;
    }

    sellingPlanGid(value) {
      const id = String(value || '');
      return id.startsWith('gid://') ? id : `gid://shopify/SellingPlan/${id}`;
    }

    publish() {
      const detail = { cart: this.cart, source: 'creator-cart' };
      document.dispatchEvent(new CustomEvent('creator-cart:updated', { detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
      this.updateCount(this.cart?.totalQuantity || 0);
    }

    updateCount(count) {
      const value = Number(count) || 0;
      this.cartCount = value;
      const label = value < 100 ? String(value) : '99+';
      document.querySelectorAll('[data-cart-count], [data-oss-cart-count]').forEach((element) => {
        element.textContent = label;
        element.classList.toggle('is-empty', value === 0);
        element.closest('.cart-count-bubble')?.toggleAttribute('hidden', value === 0);
      });
      document.body?.setAttribute('data-cart-item-count', String(value));
    }

    ajaxCart(cart) {
      const lines = cart?.lines?.nodes || [];
      const items = lines.map((line) => this.ajaxLine(line));
      return {
        token: cart?.id || '',
        note: cart?.note || null,
        attributes: Object.fromEntries((cart?.attributes || []).map((attribute) => [attribute.key, attribute.value])),
        original_total_price: this.cents(cart?.cost?.subtotalAmount?.amount),
        total_price: this.cents(cart?.cost?.totalAmount?.amount),
        total_discount: 0,
        item_count: cart?.totalQuantity || 0,
        items,
        requires_shipping: true,
        currency: cart?.cost?.totalAmount?.currencyCode || config.currency
      };
    }

    ajaxLine(line) {
      const variant = line.merchandise;
      const productImage = variant.image || variant.product.featuredImage;
      const variantId = Number(variant.id.split('/').pop());
      const price = this.cents(line.cost.amountPerQuantity.amount);
      return {
        id: variantId,
        key: line.id,
        variant_id: variantId,
        quantity: line.quantity,
        title: `${variant.product.title}${variant.title === 'Default Title' ? '' : ` - ${variant.title}`}`,
        product_title: variant.product.title,
        variant_title: variant.title === 'Default Title' ? null : variant.title,
        vendor: variant.product.vendor,
        price,
        original_price: price,
        final_price: price,
        line_price: this.cents(line.cost.totalAmount.amount),
        final_line_price: this.cents(line.cost.totalAmount.amount),
        properties: Object.fromEntries((line.attributes || []).map((attribute) => [attribute.key, attribute.value])),
        url: `/products/${variant.product.handle}?variant=${variantId}`,
        image: productImage?.url || null,
        featured_image: productImage ? { url: productImage.url, alt: productImage.altText, width: productImage.width, height: productImage.height } : null,
        product_has_only_default_variant: variant.title === 'Default Title',
        requires_shipping: true,
        taxable: true
      };
    }

    cents(amount) {
      return Math.round(Number(amount || 0) * 100);
    }
  }

  const service = new CreatorCartService();
  window.CreatorCart = service;
  window.isCreatorCartMode = service.active;
  if (!service.active) return;
  document.documentElement.classList.add('creator-cart-mode');

  // 阶段二：达人模式复用原生 #halo-cart-sidebar 抽屉（不再另建 [data-creator-cart-drawer]）。
  // 只在 cart-sidebar-show 由「关→开」这一次转换时渲染达人内容；其它 body class 变化
  // （如滚动的 scroll-up/scroll-down）一律忽略，否则会在滚动时反复重渲染、重置滚动位置，
  // 也会在关闭抽屉后把 cart-sidebar-show 加回来导致蒙层关不掉。
  let wasCartSidebarOpen = false;
  const bodyClassObserver = new MutationObserver(() => {
    if (!document.documentElement.classList.contains('creator-cart-mode')) return;
    const isOpen = document.body.classList.contains('cart-sidebar-show');
    if (isOpen && !wasCartSidebarOpen) {
      service.openDrawer();
    }
    wasCartSidebarOpen = isOpen;
  });
  bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  const normalizedPathname = (input) => {
    const raw = typeof input === 'string' ? input : input?.url;
    if (!raw) return '';
    return new URL(raw, window.location.origin).pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/(?:cart|discount))/i, '');
  };

  const cartRoute = (input) => {
    const pathname = normalizedPathname(input);
    if (/\/cart\.js$/.test(pathname)) return 'get';
    // 优惠码：主题里有 6 处直接 fetch('/discount/CODE')（购物车页、工具栏车、bundle、combo、
    // 移动端结账、KOL 落地页）。在这里统一拦截，比逐个调用点加守卫可靠。
    if (/^\/discount\/[^/]+$/.test(pathname)) return 'discount';
    // routes.cart_*_url 不带 .js 后缀，主题里多处直接用它发请求，故 .js 必须可选
    const match = pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/);
    return match ? match[1] : null;
  };

  const parseBody = async (input, options = {}) => {
    let body = options.body;
    if (body === undefined && input instanceof Request) body = await input.clone().text();
    if (body instanceof FormData) return Object.fromEntries(body.entries());
    if (body instanceof URLSearchParams) return Object.fromEntries(body.entries());
    if (typeof body === 'string') {
      try { return JSON.parse(body); } catch (_error) { return Object.fromEntries(new URLSearchParams(body)); }
    }
    return body || {};
  };

  const normalizeItems = (body) => {
    if (Array.isArray(body.items)) return body.items;
    const properties = body.properties || Object.fromEntries(Object.entries(body)
      .filter(([key]) => /^properties\[.+\]$/.test(key))
      .map(([key, value]) => [key.slice(11, -1), value]));
    return [{ id: body.id, quantity: body.quantity, properties, selling_plan: body.selling_plan }];
  };

  const respond = (payload, status = 200) => new Response(JSON.stringify(payload), {
    status,
    headers: { 'Content-Type': 'application/json' }
  });

  const handleCartRequest = async (route, body = {}, pathname = '') => {
    if (route === 'get') return service.ajaxCart(await service.getCart(true));
    if (route === 'add') return service.ajaxCart(await service.add(normalizeItems(body)));
    if (route === 'clear') return service.ajaxCart(await service.clear());
    if (route === 'change') return service.ajaxCart(await service.updateLine(body.id || body.line, body.quantity));
    if (route === 'discount') {
      // 优惠码在路径里而不是 body 里：/discount/CODE
      const code = decodeURIComponent(pathname.split('/').filter(Boolean)[1] || '');
      return service.ajaxCart(await service.applyDiscountCode(code));
    }
    if (route === 'update') {
      let cart = await service.getCart();
      if (body.note !== undefined) cart = await service.updateNote(body.note);
      for (const [lineId, quantity] of Object.entries(body.updates || {})) {
        cart = await service.updateLine(lineId, quantity);
      }
      return service.ajaxCart(cart);
    }
    return service.ajaxCart(await service.getCart());
  };

  window.fetch = async (input, options = {}) => {
    const route = cartRoute(input);
    if (!route) return originalFetch(input, options);
    try {
      return respond(await handleCartRequest(route, await parseBody(input, options), normalizedPathname(input)));
    } catch (error) {
      return respond({ status: 422, message: error.message, description: error.message }, 422);
    }
  };

  const patchJquery = () => {
    if (!window.jQuery || window.jQuery.__creatorCartPatched) return false;
    const $ = window.jQuery;
    const originalAjax = $.ajax.bind($);
    $.ajax = (options, settings) => {
      const params = typeof options === 'string' ? { ...(settings || {}), url: options } : { ...(options || {}) };
      const route = cartRoute(params.url);
      if (!route) return originalAjax(options, settings);
      const deferred = $.Deferred();
      const promise = deferred.promise();
      parseBody('', { body: params.data })
        .then((body) => handleCartRequest(route, body, normalizedPathname(params.url)))
        .then((payload) => {
          params.success?.call(params.context || params, payload, 'success', promise);
          deferred.resolveWith(params.context || params, [payload, 'success', promise]);
          params.complete?.call(params.context || params, promise, 'success');
        })
        .catch((error) => {
          const errorPayload = { status: 422, message: error.message, description: error.message };
          promise.responseText = JSON.stringify(errorPayload);
          promise.responseJSON = errorPayload;
          promise.status = 422;
          params.error?.call(params.context || params, promise, 'error', error.message);
          deferred.rejectWith(params.context || params, [promise, 'error', error.message]);
          params.complete?.call(params.context || params, promise, 'error');
        });
      return promise;
    };
    window.jQuery.__creatorCartPatched = true;
    return true;
  };

  let patchAttempts = 0;
  const patchTimer = window.setInterval(() => {
    patchAttempts += 1;
    if (patchJquery() || patchAttempts > 100) window.clearInterval(patchTimer);
  }, 50);

  // 阶段二：原生抽屉的更新入口 halo.updateSidebarCart 会 fetch /cart?view=ajax_side_cart
  // 拉回原生空车 HTML，覆盖达人内容。达人模式下把它换成空操作（抽屉由 renderDrawer 负责渲染）。
  const patchSidebarCart = () => {
    if (!window.halo || typeof window.halo.updateSidebarCart !== 'function' || window.halo.__creatorSidebarPatched) return false;
    const originalUpdateSidebarCart = window.halo.updateSidebarCart.bind(window.halo);
    window.halo.updateSidebarCart = function (cart) {
      if (window.isCreatorCartMode) return;
      return originalUpdateSidebarCart(cart);
    };
    window.halo.__creatorSidebarPatched = true;
    return true;
  };

  let sidebarPatchAttempts = 0;
  const sidebarPatchTimer = window.setInterval(() => {
    sidebarPatchAttempts += 1;
    if (patchSidebarCart() || sidebarPatchAttempts > 100) window.clearInterval(sidebarPatchTimer);
  }, 50);

  if (!service.active) return;
  document.documentElement.classList.add('creator-cart-mode');

  const getStoredScaRef = () => {
    try {
      return (
        window.musetopiaScaRef ||
        parseCreatorId(sessionStorage.getItem('musetopia_utm_campaign')) ||
        parseCreatorId(localStorage.getItem('musetopia_utm_campaign')) ||
        creatorMode.scaRef ||
        ''
      );
    } catch (_error) {
      return window.musetopiaScaRef || creatorMode.scaRef || '';
    }
  };

  const buildCartUrl = () => {
    if (!config.cartUrl) return '/cart';
    const scaRef = getStoredScaRef();
    if (!scaRef) return config.cartUrl;
    try {
      const url = new URL(config.cartUrl, window.location.origin);
      url.searchParams.set('utm_campaign', buildCampaign(scaRef));
      return url.pathname + url.search + url.hash;
    } catch (_error) {
      return config.cartUrl;
    }
  };

  document.addEventListener('click', (event) => {
    const nativeCartTrigger = event.target.closest('[data-cart-sidebar]');
    if (nativeCartTrigger) {
      event.preventDefault();
      event.stopImmediatePropagation();
      service.openDrawer();
      return;
    }
    const drawerViewCart = event.target.closest('[data-creator-view-cart]');
    if (drawerViewCart) {
      event.preventDefault();
      window.location.assign(buildCartUrl());
      return;
    }
    const cartPageLink = event.target.closest('[data-creator-cart-page-link]');
    if (!cartPageLink) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(buildCartUrl() || cartPageLink.href || config.cartUrl || '/cart');
  }, true);

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const money = (value) => {
    const amount = Number(value?.amount || 0);
    const currency = value?.currencyCode || config.currency;
    try {
      return new Intl.NumberFormat(config.locale, { style: 'currency', currency }).format(amount);
    } catch (_error) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const productUrl = (line) => {
    const variant = line.merchandise;
    return `/products/${encodeURIComponent(variant.product.handle)}?variant=${variant.id.split('/').pop()}`;
  };

  // 达人抽屉的商品行/合计不再手写 markup：克隆 halo-cart-sidebar.liquid 里由
  // snippets/cart-preview-item.liquid / cart-preview-totals.liquid 渲染出的空骨架
  // （<template id="cart-preview-item-template"> / "cart-preview-totals-template"），
  // 再按选择器填值。骨架与普通模式抽屉同源，class / data 属性 / 结构天然一致。
  const stampDrawerLine = (template, line, index) => {
    const fragment = template.content.cloneNode(true);
    const row = fragment.querySelector('.previewCartItem');
    if (!row) return null;

    const variant = line.merchandise;
    const product = variant.product;
    const variantId = variant.id.split('/').pop();
    const lineId = line.id;
    const url = productUrl(line);
    const image = variant.image || product.featuredImage;
    const isDefaultVariant = variant.title === 'Default Title';

    row.setAttribute('data-line', lineId);
    row.setAttribute('data-creator-line', lineId);

    row.querySelector('.previewCartItem-image')?.setAttribute('href', url);
    const img = row.querySelector('.previewCartItem-image img');
    if (img && image) {
      img.setAttribute('src', image.url);
      img.setAttribute('srcset', image.url);
      img.setAttribute('alt', image.altText || product.title);
      if (image.width) img.setAttribute('width', image.width);
      if (image.height) img.setAttribute('height', image.height);
    } else if (img) {
      img.remove();
    }

    row.querySelectorAll('.previewCartItem-name').forEach((anchor) => {
      anchor.setAttribute('href', url);
      const text = anchor.querySelector('.text');
      if (text) text.textContent = product.title;
    });

    if (isDefaultVariant) {
      row.querySelector('.previewCartItem-options')?.remove();
    } else {
      const variantLabel = row.querySelector('.previewCartItem-variant');
      if (variantLabel) variantLabel.textContent = variant.title;
    }

    // 品牌：与普通模式同源，显示 product 的 Brand 增强字段（metafields.product.Brand），不再是 vendor
    const brandValue = (product.metafield?.value || '').trim() || product.vendor || '';
    const brandEl = row.querySelector('.previewCartItem-brand');
    if (brandEl) brandEl.textContent = brandValue;

    // 骨架里的属性 dl 是无条件渲染的空壳，达人抽屉不展示属性，摘掉
    row.querySelector('.card-properties')?.remove();

    // 价格：与页面 stampLine 同法——有价格标记（折前价 > 折后价，或商品 compare-at > 现价）时，
    // 显示 原价(划线) + 折后价。折后价 = 行小计 / 数量；划线原价优先取 compareAt，否则取折前单价。
    const quantity = Math.max(1, Number(line.quantity) || 1);
    const lineTotalCents = service.cents(line.cost.totalAmount?.amount);
    const unitCents = service.cents(line.cost.amountPerQuantity.amount);
    const discountedUnitCents = Math.round(lineTotalCents / quantity);
    const compareAtCents = service.cents(line.merchandise?.compareAtPrice?.amount);
    const currencyCode = line.cost.amountPerQuantity?.currencyCode || config.currency;
    const hasLineDiscount = unitCents * quantity > lineTotalCents;
    const hasCompareAt = compareAtCents > unitCents;
    const hasPriceMarkdown = hasLineDiscount || hasCompareAt;
    const originalCents = hasCompareAt ? compareAtCents : unitCents;
    const finalCents = hasLineDiscount ? discountedUnitCents : unitCents;
    const formatCents = (cents) => money({ amount: cents / 100, currencyCode });

    const priceEl = row.querySelector('.previewCartItem-price');
    if (priceEl) {
      priceEl.setAttribute('data-price', finalCents);
      priceEl.setAttribute('data-original-price', originalCents);
    }
    if (hasPriceMarkdown) {
      row.querySelectorAll('[data-item-final-price-display]').forEach((element) => {
        const wrapper = document.createElement('span');
        wrapper.className = 'previewCartItem-saving-price';
        wrapper.innerHTML = `<s class="before-discount-price" data-item-original-price-display>${formatCents(originalCents)}</s> <span class="discounted-price" data-item-final-price-display>${formatCents(finalCents)}</span>`;
        element.replaceWith(wrapper);
      });
    } else {
      row.querySelectorAll('[data-item-final-price-display]').forEach((element) => {
        element.textContent = formatCents(finalCents);
      });
    }

    // 数量控件：与页面 stampLine 同法——input id 去重 + data-creator-* 盖回，由捕获通道接管
    const qty = row.querySelector('cart-update-quantity');
    if (qty) {
      const input = qty.querySelector('.quantity');
      const inputId = `creator-drawer-qty-${index}`;
      qty.setAttribute('data-variant', variantId);
      if (input) {
        input.setAttribute('id', inputId);
        input.setAttribute('data-qtt-id1', inputId);
        input.setAttribute('value', line.quantity);
        input.value = line.quantity;
        input.setAttribute('data-cart-quantity-id', variantId);
        input.setAttribute('data-line', lineId);
        input.setAttribute('data-creator-quantity-input', lineId);
      }
      qty.querySelector('label[for]')?.setAttribute('for', inputId);
      const minus = qty.querySelector('.minus');
      if (minus) {
        minus.setAttribute('data-creator-quantity', lineId);
        minus.setAttribute('data-quantity', line.quantity - 1);
      }
      const plus = qty.querySelector('.plus');
      if (plus) {
        plus.setAttribute('data-creator-quantity', lineId);
        plus.setAttribute('data-quantity', line.quantity + 1);
      }
    }

    const remove = row.querySelector('.previewCartItem-remove');
    if (remove) {
      remove.setAttribute('data-cart-remove-id', variantId);
      remove.setAttribute('data-line', lineId);
      remove.setAttribute('data-creator-remove', lineId);
    }

    return fragment;
  };

  // 合计块同样与普通模式共用 snippets/cart-preview-totals.liquid。达人抽屉的骨架
  // 门控掉优惠额行/条款勾选，保留小计+总计+运费说明(静态)+结算+查看购物车；这里只填小计/总计数值。
  const stampDrawerTotals = (scope, cart) => {
    const currencyCode = cart.cost.totalAmount.currencyCode;
    const setText = (selector, value) => {
      const element = scope.querySelector(selector);
      if (element) element.textContent = value;
    };
    // 优惠额按行加总（优惠码是行级折扣，挂在 CartLine.discountAllocations 上），与页面 stampTotals 同法
    const savedCents = (cart.lines?.nodes || []).reduce(
      (sum, line) => sum + (line.discountAllocations || []).reduce((s, entry) => s + service.cents(entry.discountedAmount?.amount), 0),
      0,
    );
    const saved = savedCents / 100;
    const subtotalCents = service.cents(cart.cost.subtotalAmount?.amount);
    // 小计展示折前原价，与优惠额行、总计构成「原价 − 折扣 = 总计」
    setText('.previewCartTotals.subTotal .previewCartTotals-value', money({ amount: (subtotalCents + savedCents) / 100, currencyCode }));
    setText('.previewCartTotals.total .previewCartTotals-value', money(cart.cost.totalAmount));
    scope.querySelector('.previewCartTotals.total .previewCartTotals-value')
      ?.setAttribute('data-cart-total-value', service.cents(cart.cost.totalAmount.amount));
    const savedRow = scope.querySelector('[data-saved-discount-display]');
    if (savedRow) {
      savedRow.style.display = saved > 0 ? '' : 'none';
      setText('[data-saved-discount-value]', money({ amount: saved, currencyCode }));
    }
    // 结算按钮在骨架里已挂 data-creator-checkout，这里补 type=button 防 form 提交（抽屉无 form，防御性）
    const checkout = scope.querySelector('[data-creator-checkout]');
    if (checkout) checkout.setAttribute('type', 'button');
  };
  const renderDrawer = (cart) => {
    const sidebar = document.getElementById('halo-cart-sidebar');
    if (!sidebar) return;
    const body = sidebar.querySelector('.previewCart-wrapper');
    if (!body) return;
    const lines = cart?.lines?.nodes || [];
    // 数量角标：service.updateCount 已统一更新 [data-cart-count]（含原生抽屉的 cartCount）
    if (!lines.length) {
      body.innerHTML = `<div class="previewCart"><div class="previewCart-emptyBody text-center"><div class="empty"><span class="text">${escapeHtml(config.labels.empty)}</span></div><a class="button button-2 button-continue" href="${escapeHtml(config.continueUrl)}">${escapeHtml(config.labels.continueShopping)}</a></div></div>`;
      return;
    }
    const itemTemplate = document.getElementById('cart-preview-item-template');
    const totalsTemplate = document.getElementById('cart-preview-totals-template');
    const toolsTemplate = document.getElementById('cart-preview-tools-template');
    if (!itemTemplate || !totalsTemplate) {
      body.innerHTML = `<div class="creator-cart-error" role="alert">${escapeHtml(config.labels.requestError)}</div>`;
      return;
    }
    const list = document.createElement('ul');
    list.className = 'previewCartList list-unstyled';
    lines.forEach((line, index) => {
      const fragment = stampDrawerLine(itemTemplate, line, index);
      if (fragment) list.appendChild(fragment);
    });
    const previewCart = document.createElement('div');
    previewCart.className = 'previewCart';
    previewCart.appendChild(list);
    body.innerHTML = '';
    body.appendChild(previewCart);
    // 工具行（备注/优惠码）作为 .previewCart 与 .previewCartInfo 之间的兄弟节点
    if (toolsTemplate) body.appendChild(toolsTemplate.content.cloneNode(true));
    // 备注框回填达人车 note（模板里的 {{ cart.note }} 是原生车，达人模式下为空）
    const noteTextarea = body.querySelector('#Cart-note');
    if (noteTextarea) noteTextarea.value = cart.note || '';
    // 结算块作为 .previewCart 的兄弟节点挂到 .previewCart-wrapper，
    // 让 .previewCart 走 flex-grow:1 + overflow-y:auto 滚动、结算固定底部（对齐普通模式抽屉）。
    body.appendChild(totalsTemplate.content.cloneNode(true));
    stampDrawerTotals(body, cart);
    // 免邮进度条用达人车合计重算（原生抽屉的 .previewCartMessage 已由 Liquid 渲染出组件）
    refreshDrawerFreeShipping();
  };

  // 原生抽屉里的免邮组件是 Liquid 渲染好的，只需派发 change 让 FreeShippingMeter 用达人车合计重算。
  const refreshDrawerFreeShipping = () => {
    document.getElementById('halo-cart-sidebar')?.querySelectorAll('.previewCartMessage free-shipping-component').forEach((element) => {
      element.dispatchEvent(new Event('change'));
    });
  };

  // 复用原生抽屉：填充 #halo-cart-sidebar 的内容 + 加 cart-sidebar-show 打开（开合动画由主题原生 CSS 接管）。
  // drawingDrawer 防重入：openDrawer 内 classList.add 会触发 MutationObserver 再调 openDrawer。
  let drawingDrawer = false;
  service.openDrawer = async () => {
    if (drawingDrawer) return;
    drawingDrawer = true;
    const sidebar = document.getElementById('halo-cart-sidebar');
    if (sidebar) {
      try {
        renderDrawer(await service.getCart(true));
      } catch (error) {
        sidebar.querySelector('.previewCart-wrapper').innerHTML = `<div class="creator-cart-error" role="alert">${escapeHtml(error.message)}</div>`;
      }
      document.body.classList.add('cart-sidebar-show');
    }
    drawingDrawer = false;
  };

  // 达人模式的商品行不再手写 markup：克隆 main-cart.liquid 里由 snippets/cart-item.liquid
  // 渲染出的空骨架（<template id="cart-item-template">），再按选择器填值。骨架与普通模式的
  // 商品行同源，class / data 属性 / 结构天然一致，不会再各写一份然后漂移。
  //
  // 阶段 2 只换渲染，不换事件通道：填完值后仍把 data-creator-* 盖回对应节点，由本文件既有的
  // 捕获期监听器接管。主题自己的委托处理器（[data-cart-remove] 等）会被 stopImmediatePropagation
  // 掐断，因此不会触发 halo.updateCart 的 Section Rendering（那会去拉原生空车的 HTML）。
  const stampLine = (template, line, index) => {
    const fragment = template.content.cloneNode(true);
    const row = fragment.querySelector('.cart-item');
    if (!row) return null;

    const variant = line.merchandise;
    const product = variant.product;
    const variantId = variant.id.split('/').pop();
    const lineId = line.id;
    const url = productUrl(line);
    const image = variant.image || product.featuredImage;
    const isDefaultVariant = variant.title === 'Default Title';
    const available = variant.availableForSale !== false;
    const unitCents = service.cents(line.cost.amountPerQuantity.amount);
    // 折后单价 = 行小计 / 数量；当「折前单价×数量 > 折后行小计」说明存在价格标记
    // （商品自身划线价 compare-at，或行级折扣），价格列此时才展示 原价划线 + 折后价。
    const quantity = Math.max(1, Number(line.quantity) || 1);
    const lineTotalCents = service.cents(line.cost.totalAmount?.amount);
    const discountedUnitCents = Math.round(lineTotalCents / quantity);
    const hasPriceMarkdown = unitCents * quantity > lineTotalCents;
    // 移动端吸底栏/摘要的 Saved 只认折扣码，不认商品 compare-at（对齐普通模式）：
    // 有 code 时原价 = 折后行小计 + 折扣码金额，再除以数量；无 code 时原价 = 折后单价。
    const codeSavedCents = (line.discountAllocations || []).reduce(
      (sum, entry) => sum + service.cents(entry.discountedAmount?.amount),
      0,
    );
    const hasCodeDiscount = codeSavedCents > 0;
    const priceBeforeCodeCents = Math.round((lineTotalCents + codeSavedCents) / quantity);

    // 骨架里的 cart_selling_array 脚本在 template 内是惰性的，但克隆插入文档后会执行，直接摘掉
    row.querySelectorAll('script').forEach((element) => element.remove());

    // 骨架是用 nil item 渲染的，所有"数据为空即成立"的分支都已展开，这里逐条纠正回真实状态
    row.setAttribute('data-line', lineId);
    row.setAttribute('data-creator-line', lineId);
    row.classList.toggle('cart-item-sold-out', !available);
    if (isDefaultVariant) {
      row.removeAttribute('data-cart-edit-productItem');
    } else {
      row.setAttribute('data-cart-edit-productItem', '');
    }
    if (available) row.querySelector('.cart-item-sold-out-badge')?.remove();

    // 左滑删除：外壳脚本的手势 handler 不自己发请求，而是按 data-line 找到本行的
    // [data-cart-remove] 再程序化点击；那次合成点击会被本文件的 data-creator-remove 捕获通道
    // 接住。所以这里只要填对标识，不需要另开事件通道。
    const swipeDelete = row.querySelector('.cart-item-swipe-delete');
    if (swipeDelete) {
      swipeDelete.setAttribute('data-cart-remove-id', variantId);
      swipeDelete.setAttribute('data-line', lineId);
    }

    // 复选框：外壳脚本用 document.querySelectorAll('.cart-item-select-checkbox') 全文档扫描，
    // 所以达人行只要把这几个 data 属性填对，勾选/全选/合计/吸底栏就被自动认领，无需另写一套。
    // 骨架里它是 disabled 的（nil item 走了 else 分支），在售时要改回 checked。
    const checkbox = row.querySelector('.cart-item-select-checkbox');
    if (checkbox) {
      checkbox.setAttribute('data-variant-id', variantId);
      checkbox.setAttribute('data-line', lineId);
      checkbox.setAttribute('data-price', discountedUnitCents);
      checkbox.setAttribute('data-original-price', hasCodeDiscount ? priceBeforeCodeCents : discountedUnitCents);
      checkbox.setAttribute('data-quantity', line.quantity);
      checkbox.setAttribute('aria-label', `Select ${product.title}`);
      if (available) {
        checkbox.removeAttribute('disabled');
        checkbox.setAttribute('checked', '');
        checkbox.checked = true;
      }
    }

    row.querySelector('.cart-item-image')?.setAttribute('href', url);
    const img = row.querySelector('.cart-item-image img');
    if (img && image) {
      img.setAttribute('src', image.url);
      img.setAttribute('srcset', image.url);
      img.setAttribute('alt', image.altText || product.title);
      if (image.width) img.setAttribute('width', image.width);
      if (image.height) img.setAttribute('height', image.height);
    } else if (img) {
      img.remove();
    }

    row.querySelectorAll('.cart-item-name a').forEach((anchor) => anchor.setAttribute('href', url));
    const titleText = row.querySelector('.cart-item-name .text');
    if (titleText) titleText.textContent = product.title;
    // 与普通模式同源：品牌显示 product 的 Brand 增强字段（metafields.product.Brand），不再是 vendor
    const brandValue = (product.metafield?.value || '').trim() || product.vendor || '';
    row.querySelectorAll('.cart-item-brand-mobile, .cart-item-vendor').forEach((element) => {
      element.textContent = brandValue;
    });

    if (isDefaultVariant) {
      row.querySelector('.cart-item-option')?.remove();
      row.querySelector('.cart-item-variant-pill')?.remove();
    } else {
      const variantLabel = row.querySelector('.cart-item-variant');
      if (variantLabel) variantLabel.textContent = variant.title;
      const pill = row.querySelector('.cart-item-variant-pill');
      if (pill) {
        pill.setAttribute('data-product-handle', product.handle);
        pill.setAttribute('data-variant-id', variantId);
        pill.setAttribute('data-line', lineId);
        pill.setAttribute('data-quantity', line.quantity);
        const pillText = pill.querySelector('.pill-text');
        if (pillText) pillText.textContent = variant.title;
      }
      const edit = row.querySelector('.cart-item-edit');
      if (edit) {
        edit.setAttribute('data-edit-cart-url', `${url}&view=ajax_edit_cart`);
        edit.setAttribute('data-edit-cart-id', variantId);
        edit.setAttribute('data-edit-cart-quantity', line.quantity);
        edit.setAttribute('data-line', lineId);
      }
    }

    // 骨架里只有"无折扣"那条价格分支（cartFields 未取 compareAtPrice，达人模式重构前也不展示折扣）
    row.querySelectorAll('[data-item-final-price-display]').forEach((element) => {
      element.textContent = money(line.cost.amountPerQuantity);
    });
    row.querySelectorAll('[data-item-price-with-quantity-display]').forEach((element) => {
      element.textContent = money(line.cost.totalAmount);
    });
    const lineTotal = row.querySelector('.cart-item-total');
    if (lineTotal) {
      lineTotal.setAttribute('data-price', discountedUnitCents);
      lineTotal.setAttribute('data-original-price', hasCodeDiscount ? priceBeforeCodeCents : discountedUnitCents);
    }

    // 商品自身的划线价（compare-at）或行级优惠，都会造成「折前单价 > 折后单价」，视为价格标记。
    // 有标记时，价格列显示 原价(划线) + 折后价，与普通模式一致。
    // 2026-07 Storefront API：amountPerQuantity 返回折前单价，totalAmount 是折后行小计，
    // 所以：折后单价 = totalAmount / 数量；原价（折前）= amountPerQuantity。
    // 这里只追加展示元素；勾选/行小计的 data-price / data-original-price（驱动移动端 Saved）
    // 已在上面按「只认折扣码」回填，与普通模式一致，不在本块重复写入。
    // 行小计列与普通模式一致：只显示折后总价（totalAmount，已是折后），不显示原价划线。
    if (hasPriceMarkdown) {
      const currencyCode = line.cost.amountPerQuantity?.currencyCode || config.currency;
      const formatCents = (cents) => money({ amount: cents / 100, currencyCode });

      // 折后单价：amountPerQuantity 是折前价，这里用「行小计 / 数量」覆盖为折后单价
      row.querySelectorAll('[data-item-final-price-display]').forEach((element) => {
        element.textContent = formatCents(discountedUnitCents);
      });

      // 价格列：原价划线 + 折后价（与普通模式一致）
      const priceWrapper = row.querySelector('.cart-item__price-wrapper');
      if (priceWrapper) {
        priceWrapper.insertAdjacentHTML('afterbegin', `<s class="cart-item__old-price price price--end" data-creator-original-price>${formatCents(unitCents)}</s> `);
      }
      // 移动端价格行：折后价 + 原价划线（与普通模式 cart-price-sale / cart-price-compare 同序）
      const priceRow = row.querySelector('.cart-item-price-row');
      if (priceRow) {
        const regular = priceRow.querySelector('.cart-price-regular');
        if (regular) {
          regular.classList.remove('cart-price-regular');
          regular.classList.add('cart-price-sale');
        }
        priceRow.insertAdjacentHTML('beforeend', `<s class="cart-price-compare" data-creator-original-price>${formatCents(unitCents)}</s>`);
      }
    }

    // 骨架含移动端和桌面端两套数量控件，input id 必须逐个去重，否则同页 id 冲突、label 也指错
    row.querySelectorAll('cart-update-quantity').forEach((element, scopeIndex) => {
      const inputId = `creator-cart-qty-${index}-${scopeIndex}`;
      element.setAttribute('data-variant', variantId);
      const input = element.querySelector('.cart-item-qty-input');
      if (input) {
        input.setAttribute('id', inputId);
        input.setAttribute('data-qtt-id1', inputId);
        input.setAttribute('value', line.quantity);
        input.value = line.quantity;
        input.setAttribute('data-cart-quantity-id', variantId);
        input.setAttribute('data-line', lineId);
        input.setAttribute('data-creator-quantity-input', lineId);
        if (available) input.removeAttribute('disabled');
      }
      element.querySelector('label[for]')?.setAttribute('for', inputId);
      const minus = element.querySelector('.minus');
      if (minus) {
        minus.setAttribute('data-creator-quantity', lineId);
        minus.setAttribute('data-quantity', line.quantity - 1);
      }
      const plus = element.querySelector('.plus');
      if (plus) {
        plus.setAttribute('data-creator-quantity', lineId);
        plus.setAttribute('data-quantity', line.quantity + 1);
      }
    });

    row.querySelectorAll('.cart-remove').forEach((element) => {
      element.setAttribute('data-cart-remove-id', variantId);
      element.setAttribute('data-line', lineId);
      element.setAttribute('data-creator-remove', lineId);
    });

    return fragment;
  };

  // 合计块同样与普通模式共用 snippets/cart-totals.liquid，不再手写。
  // 达人模式下只有运费估算不可用（达人车是独立的 Storefront cart，估运费要走另一套），
  // 由 assets/component-creator-cart.css 隐藏；优惠码、备注、条款勾选、结算都已接通。
  const stampTotals = (scope, cart) => {
    const currencyCode = cart.cost.totalAmount.currencyCode;
    const setText = (selector, value) => {
      const element = scope.querySelector(selector);
      if (element) element.textContent = value;
    };

    // 优惠额按行加总。优惠码是行级折扣，挂在 CartLine.discountAllocations 上；cart 级的
    // discountAllocations 在 2026-07 只返回订单级折扣、对行级码是空的（这也是之前「总价算了
    // 折扣但 Discount 行不显示」的原因）。不能用「小计 − 总计」——那个差值里还含税费和运费。
    const savedCents = (cart.lines?.nodes || []).reduce(
      (sum, line) => sum + (line.discountAllocations || [])
        .reduce((s, entry) => s + service.cents(entry.discountedAmount?.amount), 0),
      0,
    );
    const saved = savedCents / 100;

    // 小计展示折前原价，与 Discount 行、总计构成「原价 − 折扣 = 总计」的完整关系
    const subtotalCents = service.cents(cart.cost.subtotalAmount?.amount);
    setText('.cart-total-subtotal .cart-total-value .text', money({ amount: (subtotalCents + savedCents) / 100, currencyCode }));
    setText('.cart-total-grandtotal .cart-total-value .text', money(cart.cost.totalAmount));
    scope.querySelector('.cart-total-grandtotal .cart-total-value')
      ?.setAttribute('data-cart-total-value', service.cents(cart.cost.totalAmount.amount));

    const savedRow = scope.querySelector('[data-saved-discount-display]');
    if (savedRow) {
      savedRow.style.display = saved > 0 ? '' : 'none';
      setText('[data-saved-discount-value]', money({ amount: saved, currencyCode }));
    }

    // 结算按钮：普通模式由 cart.js 在页面加载时按 id 绑定，而达人模式下那一刻这个节点还不存在，
    // 所以改挂本文件的 data-creator-checkout 捕获通道。它在 form 内且原本没有 type，
    // 默认是 submit，必须显式改成 button，否则点击会提交表单跳走。
    const checkout = scope.querySelector('#cart-checkout');
    if (checkout) {
      checkout.setAttribute('type', 'button');
      checkout.setAttribute('data-creator-checkout', '');
    }
    // 同理：在优惠码输入框里按回车也会提交这个 form。
    // 本函数每次 renderPage 都会跑，只有这一处不是幂等的，用标记挡住重复绑定。
    const form = scope.querySelector('.cart-total form');
    if (form && !form.dataset.creatorSubmitBlocked) {
      form.dataset.creatorSubmitBlocked = 'true';
      form.addEventListener('submit', (event) => event.preventDefault());
    }

    // 达人模式：Continue shopping 应跳回 oss-html 达人页（config.continueUrl），
    // 覆盖 section 的 custom_button_link 与 history.back() 分支；普通模式不受影响。
    const continueButton = scope.querySelector('.button-continue');
    if (continueButton && config.continueUrl) {
      continueButton.setAttribute('href', config.continueUrl);
      continueButton.removeAttribute('role');
      continueButton.removeAttribute('aria-disabled');
      continueButton.removeAttribute('onclick');
    }
  };

  const renderPage = (root, cart) => {
    const lines = cart?.lines?.nodes || [];
    if (!lines.length) {
      root.innerHTML = `<div class="cart-content-empty text-center"><span class="cart-text">${escapeHtml(config.labels.empty)}</span><div class="cart-actions"><a class="button button-2 button-continue" href="${escapeHtml(config.continueUrl)}">${escapeHtml(config.labels.continueShopping)}</a></div></div>`;
      notifyShell();
      refreshFreeShipping();
      return;
    }

    // 商品列表每次全量重建；合计块只建一次，之后只更新数值。
    // 不能一起重建：移动端的 handleElementRelocation 会把优惠码输入框从合计块搬进
    // .mobile-main-addons 卡片，重建会产生第二个 #cart-coupon-code（重复 id），还会抹掉用户输入。
    let wrapper = root.querySelector('.cart-content-wrapper');
    if (!wrapper) {
      root.innerHTML = `<div class="cart-content-wrapper clearfix"><div class="cart-content-item" data-cart-content><div class="cart"><div class="cart-header"><div class="cart-header-item cart-header-info">${escapeHtml(config.labels.product)}</div><div class="cart-header-item cart-header-price">${escapeHtml(config.labels.price)}</div><div class="cart-header-item cart-header-quantity">${escapeHtml(config.labels.quantity)}</div><div class="cart-header-item cart-header-total">${escapeHtml(config.labels.totalHeading || config.labels.total)}</div><div class="cart-header-item cart-header-remove"></div></div><div class="cart-list"></div></div></div></div>`;
      wrapper = root.querySelector('.cart-content-wrapper');
      const totalsTemplate = document.getElementById('cart-totals-template');
      if (totalsTemplate && wrapper) wrapper.appendChild(totalsTemplate.content.cloneNode(true));
      // 移动端优惠码卡片：桌面端合计块在移动端被 CSS 隐藏（component-cart.css 的
      // .cart-content-item[data-cart-total] { display:none }），优惠码输入框靠外壳脚本的
      // handleElementRelocation 搬进 #mobile-main-coupon-container 才可见。这个容器在普通模式
      // 由 cart.item_count 守卫渲染，达人模式里可能不存在，这里克隆惰性 skeleton 补上。
      const noteTemplate = document.getElementById('cart-note-template');
      const couponTemplate = document.getElementById('cart-mobile-coupon-template');
      const trustTemplate = document.getElementById('cart-trust-template');
      const content = wrapper.querySelector('[data-cart-content]');
      if (noteTemplate && content) content.appendChild(noteTemplate.content.cloneNode(true));
      if (couponTemplate && content) content.appendChild(couponTemplate.content.cloneNode(true));
      if (trustTemplate && content) content.appendChild(trustTemplate.content.cloneNode(true));
    }

    const template = document.getElementById('cart-item-template');
    const list = root.querySelector('.cart-list');
    if (!template || !list) return;
    list.innerHTML = '';
    lines.forEach((line, index) => {
      const fragment = stampLine(template, line, index);
      if (fragment) list.appendChild(fragment);
    });

    if (wrapper) stampTotals(wrapper, cart);
    notifyShell();
    refreshFreeShipping();
  };

  // 免邮进度：达人车是独立 Storefront 车，/cart.js 已被本文件拦截返回达人车数据。
  // free-shipping-message.js 的 FreeShippingMeter 在收到 'change' 事件时重新取 cart 计算，
  // 这里在每次 renderPage（含空车）后派发一次，让进度条跟着达人车合计实时更新。
  const refreshFreeShipping = () => {
    document.querySelectorAll('.cart-message free-shipping-component').forEach((element) => {
      element.dispatchEvent(new Event('change'));
    });
  };

  // 外壳脚本监听的是 cart-update（注意不是本文件 publish() 广播的 cart:updated），
  // 收到后重算勾选状态、合计与吸底栏。达人行每次重渲染后都要广播一次，否则数字会停在上一次。
  const notifyShell = () => document.dispatchEvent(new CustomEvent('cart-update'));

  // 移动端外壳（吸底结算栏 + 摘要抽屉 + 驱动它们的脚本）与普通模式共用
  // snippets/cart-mobile-shell.liquid，由 main-cart.liquid 的 cart.item_count 守卫渲染。
  // 注意这个守卫看的是【原生车】，达人模式下它不一定不成立：
  //   原生车非空 -> 外壳已经在页面里了，这里直接复用（外壳脚本是纯 document 驱动的，
  //                 不关心自己属于哪份车，原生行已被 pruneNativeCartDom 清掉）
  //   原生车为空 -> 守卫不成立，克隆惰性 template 挂进 body（吸底栏和抽屉都是 fixed 定位）
  //
  // 两种情况都必须只有一份：外壳脚本注册了 18 个监听器且没有任何幂等保护（无 flag、无 once、
  // 无先 removeEventListener），挂两份会让所有 handler 双触发。克隆的 script 在插入文档时执行，
  // 脚本自身的 readyState 分支会立即完成初始化。
  const mountMobileShell = () => {
    const template = document.getElementById('cart-mobile-shell-template');
    if (!template || document.getElementById('MobileStickyCartFooter')) return;
    document.body.appendChild(template.content.cloneNode(true));
  };

  // 达人模式下 Liquid 依然渲染了一整份【原生车】的购物车 DOM —— main-cart.liquid 的
  // cart.item_count 守卫看的是原生车，而原生车不一定是空的（访客之前在普通模式加过购物车就有货）。
  // CSS 只是把 [data-cart] 藏起来了，节点仍在文档里，于是：
  //   1. 外壳脚本所有扫描都是 document 级（.cart-item / .cart-item-select-checkbox），
  //      原生行会被算进吸底栏的件数与金额 —— 达人车的数字上叠了一份普通车的数据
  //   2. #cart-coupon-code / #cart-checkout / #cart_conditions / #cart-note 各有两份，
  //      getElementById 命中的是藏起来的那一份原生节点
  // 这些节点在达人模式下没有任何用途，直接清空容器，让所有 document 级查询自然只看到达人行。
  // 必须同步执行且早于外壳脚本的 DOMContentLoaded 初始化：本文件是 defer 加载的，
  // 执行时 readyState 已是 interactive，DOM 齐全而 DOMContentLoaded 尚未触发，正好赶在前面。
  const pruneNativeCartDom = () => {
    const nativeCart = document.querySelector('[data-cart]');
    if (nativeCart) nativeCart.innerHTML = '';
  };

  const initCreatorCartPage = async () => {
    if (!/\/cart\/?$/.test(window.location.pathname)) return;
    const root = document.querySelector('[data-creator-cart-root]');
    if (!root) return;
    pruneNativeCartDom();
    root.hidden = false;
    root.innerHTML = `<div class="creator-cart-loading" role="status">${escapeHtml(config.labels.loading)}</div>`;
    try {
      const cart = await service.getCart(true);
      renderPage(root, cart);
      // 必须在 renderPage 之后：外壳脚本一插入就会扫 .cart-item 初始化勾选状态与吸底栏
      mountMobileShell();
      service.updateCount(cart?.totalQuantity || 0);
    } catch (error) {
      root.innerHTML = `<div class="creator-cart-error" role="alert">${escapeHtml(error.message)}</div>`;
    }
  };

  const initCreatorCartCount = async () => {
    // Clear the server-rendered native-cart count before the isolated cart is loaded.
    service.updateCount(0);
    try {
      const cart = await service.getCart(true);
      service.updateCount(cart?.totalQuantity || 0);
    } catch (_error) {
      service.updateCount(0);
    }
  };

  let checkoutInFlight = false;
  document.addEventListener('click', async (event) => {
    const quantity = event.target.closest('[data-creator-quantity]');
    const remove = event.target.closest('[data-creator-remove]');
    const checkout = event.target.closest('[data-creator-checkout]');
    const quickCheckout = event.target.closest('.shopify-payment-button__button, .shopify-payment-button__more-options, [data-testid="Checkout-button"]');
    if (!quantity && !remove && !checkout && !quickCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    // 防双击/快速连点并发：结算类动作加一次性锁，避免重复 service.add / 重复跳转。
    if (checkout || quickCheckout) {
      if (checkoutInFlight) return;
      checkoutInFlight = true;
    }
    try {
      if (quantity) await service.updateLine(quantity.dataset.creatorQuantity, quantity.dataset.quantity);
      if (remove) await service.removeLine(remove.dataset.creatorRemove);
      if (quickCheckout) {
        // 对齐 Shopify 原生 Buy it now：只买当前单品，不碰达人购物车。
        const productForm = quickCheckout.closest('form[action*="/cart/add"]');
        if (!productForm) throw new Error(config.labels.requestError);
        const formData = Object.fromEntries(new FormData(productForm).entries());
        const cart = await service.createCheckoutCart(normalizeItems(formData));
        const checkoutUrl = service.checkoutUrlFor(cart);
        if (checkoutUrl) window.location.assign(checkoutUrl);
        return;
      }
      if (checkout) {
        // 与普通模式 cart.js 的 handleToCheckoutPage 对齐：先提交优惠码和备注，再跳转。
        // 备注框由 cart-note-template 克隆进 [data-cart-content]（pruneNativeCartDom 已清掉原生那份），
        // 这里读得到就提交。
        const couponCode = document.getElementById('cart-coupon-code')?.value?.trim();
        if (couponCode) await service.applyDiscountCode(couponCode);
        const note = document.getElementById('cart-note')?.value;
        if (note) await service.updateNote(note);
        const cart = await service.getCart(true);
        const checkoutUrl = service.checkoutUrlFor(cart);
        if (checkoutUrl) window.location.assign(checkoutUrl);
        return;
      }
      const root = document.querySelector('[data-creator-cart-root]');
      if (root) renderPage(root, service.cart);
      if (document.body.classList.contains('cart-sidebar-show')) renderDrawer(service.cart);
    } catch (error) {
      window.alert(error.message);
    } finally {
      if (checkout || quickCheckout) checkoutInFlight = false;
    }
  }, true);

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-creator-quantity-input]');
    if (!input) return;
    try {
      await service.updateLine(input.dataset.creatorQuantityInput, input.value);
      const root = document.querySelector('[data-creator-cart-root]');
      if (root) renderPage(root, service.cart);
    } catch (error) {
      window.alert(error.message);
    }
  }, true);

  document.addEventListener('creator-cart:updated', (event) => {
    const root = document.querySelector('[data-creator-cart-root]');
    if (root && event.detail?.cart) renderPage(root, event.detail.cart);
    if (document.body.classList.contains('cart-sidebar-show') && event.detail?.cart) renderDrawer(event.detail.cart);
  });

  const initCreatorCart = () => {
    initCreatorCartPage();
    initCreatorCartCount();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCreatorCart, { once: true });
  else initCreatorCart();

})();
